<script setup>
import { computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PolicyPageHeader from './pages/components/PolicyPageHeader.vue';

const router = useRouter();
const store = useStore();
const { t } = useI18n();

const assignmentPolicies = useMapGetter(
  'assignmentPolicies/getAssignmentPolicies'
);
const assignmentUiFlags = useMapGetter('assignmentPolicies/getUIFlags');
const capacityPolicies = useMapGetter(
  'agentCapacityPolicies/getAgentCapacityPolicies'
);
const capacityUiFlags = useMapGetter('agentCapacityPolicies/getUIFlags');

// Counts are hidden when a list could not be loaded instead of showing zero.
const failedCounts = reactive({ assignment: false, capacity: false });

const countLabel = (list, isFetching, failed) => {
  if (failed) return '';
  const count = list?.length || 0;
  if (isFetching && !count) {
    return t('ASSIGNMENT_POLICY.REDESIGN.COMMON.LOADING');
  }
  return t('ASSIGNMENT_POLICY.REDESIGN.COMMON.COUNT', { n: count }, count);
};

const areas = computed(() => [
  {
    key: 'agent_assignment_policy_index',
    createKey: 'agent_assignment_policy_create',
    icon: 'i-hugeicons-arrow-data-transfer-horizontal',
    title: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.TITLE'),
    description: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.DESCRIPTION'),
    count: countLabel(
      assignmentPolicies.value,
      assignmentUiFlags.value?.isFetching,
      failedCounts.assignment
    ),
    features: [
      {
        icon: 'i-hugeicons-user-switch',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.0'),
      },
      {
        icon: 'i-hugeicons-balance-scale',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.1'),
      },
      {
        icon: 'i-hugeicons-inbox',
        label: t('ASSIGNMENT_POLICY.INDEX.ASSIGNMENT_POLICY.FEATURES.2'),
      },
    ],
  },
  {
    key: 'agent_capacity_policy_index',
    createKey: 'agent_capacity_policy_create',
    icon: 'i-hugeicons-dashboard-speed-02',
    title: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.TITLE'),
    description: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.DESCRIPTION'),
    count: countLabel(
      capacityPolicies.value,
      capacityUiFlags.value?.isFetching,
      failedCounts.capacity
    ),
    features: [
      {
        icon: 'i-hugeicons-queue-02',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.0'),
      },
      {
        icon: 'i-hugeicons-filter',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.1'),
      },
      {
        icon: 'i-hugeicons-user-multiple-02',
        label: t('ASSIGNMENT_POLICY.INDEX.AGENT_CAPACITY_POLICY.FEATURES.2'),
      },
    ],
  },
]);

const howItWorks = computed(() => [
  {
    icon: 'i-hugeicons-arrow-data-transfer-horizontal',
    text: t('ASSIGNMENT_POLICY.REDESIGN.INDEX.HOW_ASSIGNMENT'),
  },
  {
    icon: 'i-hugeicons-dashboard-speed-02',
    text: t('ASSIGNMENT_POLICY.REDESIGN.INDEX.HOW_CAPACITY'),
  },
  {
    icon: 'i-hugeicons-user-check-01',
    text: t('ASSIGNMENT_POLICY.REDESIGN.INDEX.HOW_MANUAL'),
  },
]);

const handleCreate = routeName => {
  router.push({ name: routeName });
};

// Read-only fetches used only to show how many policies exist in each area.
onMounted(() => {
  store.dispatch('assignmentPolicies/get').catch(() => {
    failedCounts.assignment = true;
  });
  store.dispatch('agentCapacityPolicies/get').catch(() => {
    failedCounts.capacity = true;
  });
});
</script>

<template>
  <SettingsLayout :no-records-found="false">
    <template #header>
      <PolicyPageHeader
        icon="i-hugeicons-user-settings-01"
        :title="$t('ASSIGNMENT_POLICY.INDEX.HEADER.TITLE')"
        :description="$t('ASSIGNMENT_POLICY.REDESIGN.INDEX.INTRO')"
      />
    </template>

    <template #body>
      <div class="flex flex-col gap-4">
        <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <article
            v-for="area in areas"
            :key="area.key"
            class="group relative flex min-w-0 flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-2 p-5 transition-colors hover:border-n-strong motion-reduce:transition-none"
            :data-test-id="`assignment-area-${area.key}`"
          >
            <router-link
              :to="{ name: area.key }"
              :aria-label="area.title"
              class="absolute inset-0 z-10 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
            />
            <div class="flex items-start justify-between gap-3">
              <div class="flex min-w-0 items-start gap-3">
                <span
                  class="grid size-11 shrink-0 place-items-center rounded-xl bg-n-brand/10 text-n-brand"
                  aria-hidden="true"
                >
                  <Icon :icon="area.icon" class="size-6" />
                </span>
                <div class="min-w-0">
                  <h2 class="mb-1 text-base font-semibold text-n-slate-12">
                    {{ area.title }}
                  </h2>
                  <p class="mb-0 text-sm leading-relaxed text-n-slate-11">
                    {{ area.description }}
                  </p>
                </div>
              </div>
              <span
                v-if="area.count"
                class="shrink-0 rounded-md bg-n-alpha-2 px-2.5 py-1 text-xs font-medium text-n-slate-12"
                data-test-id="assignment-area-count"
              >
                {{ area.count }}
              </span>
            </div>

            <ul class="mb-0 flex flex-col gap-2.5">
              <li
                v-for="feature in area.features"
                :key="feature.label"
                class="flex items-start gap-2.5 text-sm text-n-slate-12"
              >
                <Icon
                  :icon="feature.icon"
                  class="mt-0.5 size-4 shrink-0 text-n-slate-11"
                />
                {{ feature.label }}
              </li>
            </ul>

            <div
              class="mt-auto flex flex-wrap items-center justify-between gap-2 border-t border-n-weak pt-4"
            >
              <span
                class="flex items-center gap-1 text-sm font-medium text-n-slate-11 group-hover:text-n-slate-12"
              >
                {{ $t('ASSIGNMENT_POLICY.REDESIGN.INDEX.OPEN') }}
                <Icon icon="i-hugeicons-arrow-right-02" class="size-4" />
              </span>
              <Button
                sm
                slate
                faded
                icon="i-hugeicons-add-01"
                class="relative z-20"
                :label="$t('ASSIGNMENT_POLICY.REDESIGN.INDEX.NEW')"
                @click="handleCreate(area.createKey)"
              />
            </div>
          </article>
        </div>

        <section
          class="rounded-xl border border-n-weak p-5"
          :aria-label="$t('ASSIGNMENT_POLICY.REDESIGN.INDEX.HOW_TITLE')"
        >
          <h2
            class="mb-3 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <Icon icon="i-hugeicons-idea-01" class="size-4 text-n-slate-11" />
            {{ $t('ASSIGNMENT_POLICY.REDESIGN.INDEX.HOW_TITLE') }}
          </h2>
          <ul class="mb-0 grid grid-cols-1 gap-3 md:grid-cols-3">
            <li
              v-for="item in howItWorks"
              :key="item.icon"
              class="flex items-start gap-2.5 rounded-lg bg-n-alpha-1 p-3 text-sm leading-relaxed text-n-slate-11"
            >
              <Icon
                :icon="item.icon"
                class="mt-0.5 size-4 shrink-0 text-n-slate-11"
              />
              {{ item.text }}
            </li>
          </ul>
        </section>
      </div>
    </template>
  </SettingsLayout>
</template>
